// Sentry browser SDK init for the Liminl frontend.
//
// Three concerns this file handles:
//   1. Init the SDK with environment/release tagging + breadcrumb redaction.
//   2. Export a slim, dependency-free ErrorBoundary used at the app root.
//   3. Forward captured errors to Sentry when (and only when) it's loaded.
//
// The init is a NO-OP when VITE_SENTRY_DSN is unset - keeps local dev silent
// unless the developer explicitly opts in via .env.local.
//
// **Bundling note:** `@sentry/react` is dynamically imported inside
// `initSentry`, so the ~30 KB gz SDK is split into its own chunk that only
// loads when the DSN is configured. Anonymous landing visitors never download
// it. We also defer the import off the first-paint critical path via
// requestIdleCallback.

import { Component, type ErrorInfo, type ReactNode } from "react";

let sentryCaptureException: ((error: unknown) => void) | null = null;
const pendingErrors: unknown[] = [];
const MAX_PENDING_ERRORS = 20;

/**
 * Initialize Sentry. Safe to call once at app startup. No-op when
 * VITE_SENTRY_DSN is not set so local dev stays out of the prod project.
 *
 * Implementation: dynamically imports `@sentry/react` so the SDK chunk is
 * only fetched when actually needed. Returns a promise mostly for tests -
 * callers don't need to await it.
 */
export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  const Sentry = await import("@sentry/react");

  Sentry.init({
    dsn,
    environment: import.meta.env.VITE_APP_ENV ?? "development",
    release: import.meta.env.VITE_RELEASE || undefined,

    // We never want full PII auto-collected; we add user context explicitly
    // via Sentry.setUser once the auth bootstrap resolves.
    sendDefaultPii: false,

    integrations: [Sentry.browserTracingIntegration()],

    // 10% trace sample. The earlier 100% was a temporary diagnostic while
    // we were chasing the slow dashboard load. The bottleneck was identified
    // as backend latency on /api/home/* (see hand-off note to backend team);
    // staying at 100% in prod burns the free-tier transaction quota fast.
    tracesSampleRate: 0.1,

    // Only propagate trace headers to our own API surfaces. Stops
    // sentry-trace headers leaking to third-party CDNs/analytics.
    tracePropagationTargets: [
      /^http:\/\/localhost:4000\/api/,
      /^https:\/\/api\.goliminl\.com\/api/,
    ],

    // Drop known noise upstream of the breadcrumb/event pipeline.
    ignoreErrors: [
      // Benign ResizeObserver chatter shipped by Chrome.
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Sentry's auto-capture of non-Error throws - almost always
      // browser-extension noise we can't act on.
      "Non-Error promise rejection captured",
    ],

    // Strip x-session-id from fetch/xhr breadcrumb headers. We treat
    // sessionId as bearer-equivalent; never want it in a Sentry payload.
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        const data = breadcrumb.data as
          | { request_headers?: Record<string, string> }
          | undefined;
        if (data?.request_headers && "x-session-id" in data.request_headers) {
          const { ["x-session-id"]: _stripped, ...rest } = data.request_headers;
          data.request_headers = rest;
        }
      }
      return breadcrumb;
    },

    // Drop request body data from sensitive paths. Even at sendDefaultPii:
    // false, attached request bodies can carry PII; better safe.
    beforeSend(event) {
      const url = event.request?.url ?? "";
      if (/\/api\/(auth|network\/contacts|onboarding|me)/.test(url)) {
        if (event.request) {
          delete event.request.data;
          delete event.request.cookies;
        }
      }
      return event;
    },
  });

  // Stash the capture function so the local ErrorBoundary can forward
  // render-phase errors without re-importing the SDK.
  sentryCaptureException = (error) => Sentry.captureException(error);
  pendingErrors.splice(0).forEach((error) => sentryCaptureException?.(error));
}

/**
 * Send a caught error to Sentry if it's loaded. No-op otherwise. Exported so
 * non-root error boundaries (RouteErrorBoundary, etc.) can report without
 * pulling @sentry/react into their own chunk.
 */
export function reportError(error: unknown): void {
  if (sentryCaptureException) {
    sentryCaptureException(error);
    return;
  }
  if (pendingErrors.length >= MAX_PENDING_ERRORS) pendingErrors.shift();
  pendingErrors.push(error);
}

/**
 * Lightweight, dependency-free error boundary used at the app root. Forwards
 * caught errors to Sentry only if the SDK has been initialized - otherwise
 * the boundary still renders the fallback so users see something useful.
 */
export class AppErrorBoundary extends Component<
  { children: ReactNode; fallback: ReactNode },
  { hasError: boolean }
> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (sentryCaptureException) {
      sentryCaptureException(error);
    }
    // Always log so devs see the component stack locally - Sentry has its
    // own React stack capture so this is redundant in prod but harmless.
    console.error("Unhandled render error", error, info);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
